import React, { useEffect, useState } from "react";
import subscriptionService from "../../api/services/subscriptionService";
import toast from "react-hot-toast";
import Loading from "../../components/Loading";

const Subscription = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [subscriptionData, setSubscriptionData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  const tabs = [
    { key: "All", label: "All" },
    { key: "Weekly", label: "Weekly" },
    { key: "Monthly", label: "Monthly" },
    { key: "NaseebiUsers", label: "Naseebi Users" },
    { key: "OtherUsers", label: "Other Users" },
  ];

  useEffect(() => {
    const getSubscriptions = async () => {
      try {
        setIsLoading(true);

        // Determine duration and emailType based on activeTab
        let duration = null;
        let emailType = null;

        if (activeTab === "Weekly") {
          duration = "Weekly";
        } else if (activeTab === "Monthly") {
          duration = "Monthly";
        } else if (activeTab === "NaseebiUsers") {
          emailType = "naseebimail";
        } else if (activeTab === "OtherUsers") {
          emailType = "other";
        }

        const response = await subscriptionService.getAllPurchasedSubscriptions(
          {
            page: currentPage,
            limit: itemsPerPage,
            duration: duration,
            emailType: emailType,
          }
        );
        if (response?.responseCode === 200) {
          setSubscriptionData(response?.subscriptions);
          if (response?.pagination) {
            setCurrentPage(response.pagination.currentPage);
            setTotalPages(response.pagination.totalPages);
            setTotalItems(response.pagination.totalItems);
            setItemsPerPage(response.pagination.itemsPerPage);
          }
          // toast.success(response?.message);
        } else {
          toast.error(response?.message);
        }
      } catch (error) {
        toast.error("Failed to fetch subscriptions");
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    getSubscriptions();
  }, [currentPage, itemsPerPage, activeTab]);

  // 🔍 Filter by search (name or email) - duration and email filtering is now done by API
  const tableData = subscriptionData.filter((sub) => {
    const fullName = `${sub.firstName || ""} ${
      sub.lastName || ""
    }`.toLowerCase();
    const email = sub.email?.toLowerCase() || "";
    const term = searchTerm.toLowerCase();
    return fullName.includes(term) || email.includes(term);
  });

  return (
    <div className="p-4 w-full">
      {isLoading ? (
        <Loading />
      ) : (
        <>
          {/* 🔹 Header Section */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            {/* Tabs */}
            <div className="flex gap-3 flex-wrap">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key);
                    setCurrentPage(1); // Reset to first page when tab changes
                  }}
                  className={`px-4 py-2 rounded ${
                    activeTab === tab.key
                      ? "bg-red-500 text-white shadow text-xs md:text-lg"
                      : "bg-gray-100 text-gray-700 text-xs md:text-lg"
                  }`}>
                  {tab.label}
                </button>
              ))}
            </div>

            {/* 🔍 Search Bar */}
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="text-xs md:text-lg border border-gray-300 rounded-md px-4 py-2 w-full md:w-64 focus:outline-none focus:ring-2 focus:ring-red-400"
            />
          </div>

          {/* 🔹 Table */}
          <div className="overflow-x-auto bg-white shadow rounded-lg">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-100 text-gray-700">
                  <th className="p-3 border-b text-xs md:text-lg">User Name</th>
                  <th className="p-3 border-b text-xs md:text-lg">Email</th>
                  <th className="p-3 border-b text-xs md:text-lg">Plan</th>
                  <th className="p-3 border-b text-xs md:text-lg">Duration</th>
                  <th className="p-3 border-b text-xs md:text-lg">Duration (Days)</th>
                  <th className="p-3 border-b text-xs md:text-lg">Price</th>
                  <th className="p-3 border-b text-xs md:text-lg">Start Date</th>
                  <th className="p-3 border-b text-xs md:text-lg">End Date</th>
                </tr>
              </thead>
              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((sub) => (
                    <tr
                      key={sub.userSubscriptionId}
                      className="hover:bg-gray-50 text-xs md:text-lg">
                      <td className="p-3 border-b font-semibold">
                        {sub.firstName} {sub.lastName}
                      </td>
                      <td className="p-3 border-b text-gray-600">
                        {sub.email}
                      </td>
                      <td className="p-3 border-b">{sub.name}</td>
                      <td className="p-3 border-b">{sub.duration}</td>
                      <td className="p-3 border-b">{sub.duration_days}</td>
                      <td className="p-3 border-b">
                        {sub.currency} {sub.amount}
                      </td>
                      <td className="p-3 border-b">
                        {sub.start_date
                          ? new Date(sub.start_date).toLocaleDateString()
                          : "-"}
                      </td>
                      <td className="p-3 border-b">
                        {sub.end_date
                          ? new Date(sub.end_date).toLocaleDateString()
                          : "-"}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="p-4 text-center text-gray-500">
                      No subscriptions found for "{activeTab}" or search term "
                      {searchTerm}".
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="text-xs md:text-sm font-medium text-gray-700">
                Showing page{" "}
                <span className="font-bold text-gray-900">{currentPage}</span>{" "}
                of <span className="font-bold text-gray-900">{totalPages}</span>{" "}
                (Total:{" "}
                <span className="font-bold text-red-600">{totalItems}</span>{" "}
                subscriptions)
              </div>
              <div className="flex gap-2 items-center justify-center">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
                    currentPage === 1
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200 "
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}>
                  Previous
                </button>
                <span className="text-xs md:text-sm text-gray-700">
                  <span className="font-bold">
                    {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)}
                  </span>{" "}
                  -{" "}
                  <span className="font-bold">
                    {Math.min(currentPage * itemsPerPage, totalItems)}
                  </span>{" "}
                  of{" "}
                  <span className="font-bold text-red-600">{totalItems}</span>
                </span>
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className={`px-4 py-2 border rounded-lg font-medium text-xs md:text-sm transition-colors ${
                    currentPage === totalPages
                      ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                      : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 hover:border-gray-400"
                  }`}>
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Subscription;
